import React, { useCallback, useEffect, useState } from "react";
import {
	Autocomplete,
	Avatar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { Close as CloseIcon, PersonRemove as PersonRemoveIcon } from "@mui/icons-material";
import { GlobalState } from "context/GlobalContext";
import {
	listProductDesignWorkspaceShares,
	removeProductDesignWorkspaceShare,
	searchProductDesignWorkspaceShareCandidates,
	upsertProductDesignWorkspaceShare,
} from "./services";

const ROLE_OPTIONS = [
	{ value: "viewer", label: "Viewer", help: "Can view and download" },
	{ value: "editor", label: "Editor", help: "Can also upload, add folders and move to Bin" },
];

const initials = (name = "") =>
	name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("") || "?";

// Drive-style "Share" dialog: add people with a role, change or remove existing access.
const ShareDialog = ({ item, onClose }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [owner, setOwner] = useState(null);
	const [shares, setShares] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [candidates, setCandidates] = useState([]);
	const [searching, setSearching] = useState(false);
	const [selectedPeople, setSelectedPeople] = useState([]);
	const [newRole, setNewRole] = useState("viewer");

	const itemPath = item?.type === "folder" ? item.fullPath : item?.fileName || item?.fullPath;
	const isFolder = item?.type === "folder";

	const notify = useCallback(
		(message, severity) => {
			setSnackBar({ ...snackBar, message, severity });
			setSnackBarOpen(true);
		},
		[setSnackBar, setSnackBarOpen, snackBar],
	);

	const loadShares = useCallback(async () => {
		if (!itemPath) return;
		setLoading(true);
		try {
			const data = await listProductDesignWorkspaceShares({ itemPath });
			setOwner(data.owner);
			setShares(data.shares || []);
		} catch (error) {
			console.error("Failed to load shares", error);
			notify(error.response?.data?.message || "Failed to load sharing settings", "error");
		} finally {
			setLoading(false);
		}
	}, [itemPath, notify]);

	useEffect(() => {
		setSelectedPeople([]);
		setSearchText("");
		setNewRole("viewer");
		loadShares();
	}, [loadShares]);

	// Debounced people search.
	useEffect(() => {
		const query = searchText.trim();
		if (query.length < 2) {
			setCandidates([]);
			return undefined;
		}
		const timer = setTimeout(async () => {
			setSearching(true);
			try {
				setCandidates(await searchProductDesignWorkspaceShareCandidates({ search: query }));
			} catch (error) {
				console.error("People search failed", error);
			} finally {
				setSearching(false);
			}
		}, 300);
		return () => clearTimeout(timer);
	}, [searchText]);

	const saveShare = async (userId, role) => {
		await upsertProductDesignWorkspaceShare({ itemPath, isFolder, userId, role });
	};

	const handleShare = async () => {
		if (!selectedPeople.length) return;
		setSaving(true);
		try {
			await Promise.all(selectedPeople.map((person) => saveShare(person.id, newRole)));
			notify(`Shared "${item.name}"`, "success");
			setSelectedPeople([]);
			setSearchText("");
			await loadShares();
		} catch (error) {
			console.error("Share failed", error);
			notify(error.response?.data?.message || "Failed to share", "error");
		} finally {
			setSaving(false);
		}
	};

	const handleRoleChange = async (share, role) => {
		setSaving(true);
		try {
			await saveShare(share.userId, role);
			await loadShares();
		} catch (error) {
			console.error("Role change failed", error);
			notify(error.response?.data?.message || "Failed to update access", "error");
		} finally {
			setSaving(false);
		}
	};

	const handleRemove = async (share) => {
		setSaving(true);
		try {
			await removeProductDesignWorkspaceShare({ shareId: share.id });
			notify(`Removed ${share.name || share.email}`, "success");
			await loadShares();
		} catch (error) {
			console.error("Remove share failed", error);
			notify(error.response?.data?.message || "Failed to remove access", "error");
		} finally {
			setSaving(false);
		}
	};

	const alreadyShared = new Set([owner?.id, ...shares.map((share) => share.userId)]);

	return (
		<Dialog open={Boolean(item)} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle sx={{ pr: 6 }}>
				Share "{item?.name}"
				<IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				<Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
					<Autocomplete
						multiple
						fullWidth
						options={candidates.filter((person) => !alreadyShared.has(person.id))}
						value={selectedPeople}
						onChange={(_event, value) => setSelectedPeople(value)}
						inputValue={searchText}
						onInputChange={(_event, value, reason) => reason !== "reset" && setSearchText(value)}
						getOptionLabel={(person) => person.name || person.email}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						filterOptions={(options) => options}
						loading={searching}
						noOptionsText={searchText.trim().length < 2 ? "Type at least 2 letters" : "No one with workspace access found"}
						renderOption={(props, person) => (
							<li {...props} key={person.id}>
								<Box>
									<Typography variant="body2">{person.name}</Typography>
									<Typography variant="caption" color="text.secondary">
										{person.email}
									</Typography>
								</Box>
							</li>
						)}
						renderInput={(params) => (
							<TextField
								{...params}
								size="small"
								label="Add people"
								placeholder="Search by name or email"
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{searching ? <CircularProgress color="inherit" size={18} /> : null}
											{params.InputProps.endAdornment}
										</>
									),
								}}
							/>
						)}
					/>
					<Select size="small" value={newRole} onChange={(event) => setNewRole(event.target.value)} sx={{ minWidth: 120 }}>
						{ROLE_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</Stack>
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
					{ROLE_OPTIONS.find((option) => option.value === newRole)?.help}
					{isFolder ? " — applies to everything inside this folder." : "."}
				</Typography>

				<Typography variant="subtitle2" sx={{ mt: 3 }}>
					People with access
				</Typography>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
						<CircularProgress size={24} />
					</Box>
				) : (
					<List dense>
						{owner && (
							<ListItem secondaryAction={<Typography variant="body2" color="text.secondary">Owner</Typography>}>
								<ListItemAvatar>
									<Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{initials(owner.name)}</Avatar>
								</ListItemAvatar>
								<ListItemText primary={owner.name || owner.email} secondary={owner.email} />
							</ListItem>
						)}
						{shares.map((share) => (
							<ListItem
								key={share.id}
								secondaryAction={
									<Stack direction="row" alignItems="center" spacing={0.5}>
										<Select
											size="small"
											variant="standard"
											disableUnderline
											value={share.role}
											disabled={saving}
											onChange={(event) => handleRoleChange(share, event.target.value)}>
											{ROLE_OPTIONS.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</Select>
										<Tooltip title="Remove access">
											<span>
												<IconButton edge="end" size="small" disabled={saving} onClick={() => handleRemove(share)}>
													<PersonRemoveIcon fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
									</Stack>
								}
								sx={{ pr: 18 }}>
								<ListItemAvatar>
									<Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{initials(share.name)}</Avatar>
								</ListItemAvatar>
								<ListItemText primary={share.name || share.email} secondary={share.email} />
							</ListItem>
						))}
						{!shares.length && (
							<Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
								Only the owner and workspace admins can see this {isFolder ? "folder" : "file"}.
							</Typography>
						)}
					</List>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Done</Button>
				<Button variant="contained" disabled={!selectedPeople.length || saving} onClick={handleShare}>
					Share
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ShareDialog;
